import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react'
import { useAuth, type Permission } from '@/contexts/AuthContext'
import { Toaster } from '@/components/ui/toaster'
import { OfflineBanner } from '@/components/OfflineBanner'

// Layouts
import PublicLayout from '@/components/layouts/PublicLayout'
import DashboardLayout from '@/components/layouts/DashboardLayout'

// Public Pages
import LandingPage from '@/pages/LandingPage'
import PricingPage from '@/pages/PricingPage'
import AestheticComparisonPage from '@/pages/AestheticComparisonPage'
import ContributorPage from '@/pages/ContributorPage'
import PublicGalleryPage from '@/pages/PublicGalleryPage'
import LoginPage from '@/pages/LoginPage'
import SignupPage from '@/pages/SignupPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'

// Protected Pages
import Dashboard from '@/pages/Dashboard'
import DropsPage from '@/pages/DropsPage'
import DropDetailPage from '@/pages/DropDetailPage'
import CreateDropPage from '@/pages/CreateDropPage'
import EditDropPage from '@/pages/EditDropPage'
import SettingsPage from '@/pages/SettingsPage'
import TeamPage from '@/pages/TeamPage'
import TeamManagePage from '@/pages/TeamManagePage'
import TeamInvitePage from '@/pages/TeamInvitePage'
import TeamAcceptPage from '@/pages/TeamAcceptPage'
import BillingPage from '@/pages/BillingPage'
import NotFoundPage from '@/pages/NotFoundPage'
import ForbiddenPage from '@/pages/ForbiddenPage'
import SmsConsentPage from '@/pages/SmsConsentPage'
import PrivacyPage from '@/pages/PrivacyPage'
import TermsPage from '@/pages/TermsPage'
import SuperAdminPage from '@/pages/SuperAdminPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth()
  const location = useLocation()

  // Show nothing while auth state is loading
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // If signed in, render children
  if (isSignedIn) {
    return <>{children}</>
  }

  // If not signed in, redirect to login with redirect_url
  const redirectUrl = encodeURIComponent(location.pathname + location.search)
  return <Navigate to={`/login?redirect_url=${redirectUrl}`} replace />
}

// Permission-based route protection
export function PermissionRoute({
  children,
  permission
}: {
  children: React.ReactNode
  permission: Permission
}) {
  const { hasPermission, isLoaded } = useAuth()

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!hasPermission(permission)) {
    return <Navigate to="/forbidden" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <>
      <OfflineBanner />
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/aesthetics" element={<AestheticComparisonPage />} />
        </Route>

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/sso-callback" element={<AuthenticateWithRedirectCallback />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/sso-callback" element={<AuthenticateWithRedirectCallback />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Contributor Route (public, token-based auth) */}
        <Route path="/c/:token" element={<ContributorPage />} />

        {/* Public Gallery Route (public, read-only access) */}
        <Route path="/gallery/:token" element={<PublicGalleryPage />} />

        {/* SMS consent policy (opt-in evidence for toll-free verification) */}
        <Route path="/sms-consent" element={<SmsConsentPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Error Pages */}
        <Route path="/forbidden" element={<ForbiddenPage />} />

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Routes accessible to all authenticated users */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/drops" element={<DropsPage />} />
          <Route path="/drops/:id" element={<DropDetailPage />} />

          {/* Routes requiring create_drop permission */}
          <Route
            path="/drops/create"
            element={
              <PermissionRoute permission="create_drop">
                <CreateDropPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/drops/:id/edit"
            element={
              <PermissionRoute permission="edit_drop">
                <EditDropPage />
              </PermissionRoute>
            }
          />

          {/* Settings - accessible to all */}
          <Route path="/settings" element={<SettingsPage />} />

          {/* Billing - requires manage_billing permission */}
          <Route
            path="/settings/billing"
            element={
              <PermissionRoute permission="manage_billing">
                <BillingPage />
              </PermissionRoute>
            }
          />

          {/* Team overview - accessible to all */}
          <Route path="/team" element={<TeamPage />} />

          {/* Team management - requires manage_team permission */}
          <Route
            path="/team/manage"
            element={
              <PermissionRoute permission="manage_team">
                <TeamManagePage />
              </PermissionRoute>
            }
          />

          {/* Team invite - requires manage_team permission */}
          <Route
            path="/team/invite"
            element={
              <PermissionRoute permission="manage_team">
                <TeamInvitePage />
              </PermissionRoute>
            }
          />

          {/* Team accept - any authenticated user (email match enforced inside) */}
          <Route path="/team/accept" element={<TeamAcceptPage />} />

          {/* Super Admin - requires super_admin permission */}
          <Route
            path="/admin"
            element={
              <PermissionRoute permission="super_admin">
                <SuperAdminPage />
              </PermissionRoute>
            }
          />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <Toaster />
    </>
  )
}

export default App
